import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const EMPTY = { full_name: "", birth_date: "", cpf: "", phone: "", email: "", notes: "", family: "" };
const MAX_ROWS = 10;

function maskCpfInput(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

function maskPhoneInput(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
}

export default function ClientFormDialog({ open, onOpenChange, client, onSaved, existingFamilies = [] }) {
  const [rows, setRows] = useState([{ ...EMPTY }]);
  const [saving, setSaving] = useState(false);

  const isEditing = !!client?.client_id;

  useEffect(() => {
    if (client) setRows([{ ...EMPTY, ...client }]);
    else setRows([{ ...EMPTY }]);
  }, [client, open]);

  const updateRow = (idx, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    setRows((prev) => [...prev, { ...EMPTY }]);
  };

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validate all rows minimally
    for (const [i, r] of rows.entries()) {
      if (!r.full_name.trim() || !r.birth_date || !r.cpf || !r.phone) {
        toast.error(`Preencha os campos obrigatórios da pessoa ${i + 1}`);
        return;
      }
    }
    setSaving(true);
    try {
      const normalize = (r) => ({
        ...r,
        cpf: r.cpf.replace(/\D/g, ""),
        email: r.email || null,
      });
      if (isEditing) {
        await api.put(`/clients/${client.client_id}`, normalize(rows[0]));
        toast.success("Cliente atualizado");
      } else if (rows.length === 1) {
        await api.post("/clients", normalize(rows[0]));
        toast.success("Cliente cadastrado");
      } else {
        const { data } = await api.post("/clients/bulk", rows.map(normalize));
        if (data.count === rows.length) {
          toast.success(`${data.count} cliente(s) cadastrado(s)`);
        } else if (data.count > 0) {
          toast.warning(
            `${data.count} cadastrado(s), ${data.errors.length} com erro: ` +
            data.errors.map((e) => `${e.full_name} (${e.reason})`).join("; "),
            { duration: 8000 }
          );
        } else {
          toast.error(
            "Nenhum cadastrado. Erros: " +
            data.errors.map((e) => `${e.full_name} (${e.reason})`).join("; ")
          );
          setSaving(false);
          return;
        }
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar cliente" : "Novo(s) cliente(s)"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Atualize os dados do cliente."
              : `Cadastre até ${MAX_ROWS} pessoas de uma vez.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {rows.map((form, idx) => (
            <div
              key={idx}
              className="rounded-xl border border-slate-200 p-4 space-y-3 relative bg-slate-50/50"
              data-testid={`client-row-${idx}`}
            >
              {!isEditing && rows.length > 1 && (
                <>
                  <div className="text-xs font-semibold text-[#0b3a82] mb-1">
                    Pessoa {idx + 1} de {rows.length}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full hover:bg-red-50 text-red-500"
                    title="Remover"
                    data-testid={`client-row-remove-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}

              <div>
                <Label>Nome completo</Label>
                <Input
                  required
                  value={form.full_name}
                  onChange={(e) => updateRow(idx, "full_name", e.target.value)}
                  data-testid={`client-form-name-${idx}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data de nascimento</Label>
                  <Input
                    type="date"
                    required
                    value={form.birth_date}
                    onChange={(e) => updateRow(idx, "birth_date", e.target.value)}
                    data-testid={`client-form-birth-${idx}`}
                  />
                </div>
                <div>
                  <Label>CPF</Label>
                  <Input
                    required
                    value={form.cpf}
                    onChange={(e) => updateRow(idx, "cpf", maskCpfInput(e.target.value))}
                    placeholder="000.000.000-00"
                    data-testid={`client-form-cpf-${idx}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telefone</Label>
                  <Input
                    required
                    value={form.phone}
                    onChange={(e) => updateRow(idx, "phone", maskPhoneInput(e.target.value))}
                    placeholder="(00) 00000-0000"
                    data-testid={`client-form-phone-${idx}`}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => updateRow(idx, "email", e.target.value)}
                    data-testid={`client-form-email-${idx}`}
                  />
                </div>
              </div>

              <div>
                <Label>Família</Label>
                <Input
                  value={form.family || ""}
                  onChange={(e) => updateRow(idx, "family", e.target.value)}
                  placeholder="Ex: Silva, Pereira (deixe vazio se não houver)"
                  list={`client-families-${idx}`}
                  data-testid={`client-form-family-${idx}`}
                />
                <datalist id={`client-families-${idx}`}>
                  {existingFamilies.map((f) => (
                    <option key={f} value={f} />
                  ))}
                </datalist>
              </div>

              <div>
                <Label>Observações</Label>
                <Textarea
                  rows={2}
                  value={form.notes || ""}
                  onChange={(e) => updateRow(idx, "notes", e.target.value)}
                  data-testid={`client-form-notes-${idx}`}
                />
              </div>
            </div>
          ))}

          {!isEditing && (
            <Button
              type="button"
              variant="outline"
              className="w-full border-dashed border-blue-300 text-[#0b3a82] hover:bg-blue-50"
              onClick={addRow}
              disabled={rows.length >= MAX_ROWS}
              data-testid="client-form-add-row"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {rows.length >= MAX_ROWS
                ? `Limite de ${MAX_ROWS} pessoas atingido`
                : `Adicionar outra pessoa (${rows.length}/${MAX_ROWS})`}
            </Button>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="brand-grad text-white"
              data-testid="client-form-submit"
            >
              {saving ? "Salvando..." : rows.length > 1 ? `Salvar ${rows.length} cadastros` : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
